import React, { Component } from "react";
import ReactDOM from "react-dom";
import { connect, Provider } from "react-redux";
import { GoogleApiWrapper } from "google-maps-react";
import config from "../../../constants/config";
import { getControlBox } from "../common/ControlBox";
import { RoundControl, MapTypeControl } from "../control/MapControls";
import MapKPIControl from "../EGKPIBox/MapKPIControl";
import MapMarker from "../marker/MapMarker";
import ReplayControl from "../replay/ReplayControl";
import DrawingControl, { setDrawingInstance } from "../geo/DrawingControl";
import AnalyzeControl from "../../analyse/AnalyzeControl";
import WorkWindowControl from "../workwindow/WorkWindowControl";
import AutoZoomToggleControl from "../control/AutoZoomToggleControl";
import LiveConsoleIcon from "../../liveConsole/LiveConsoleIcon";
import LiveConsoleControl from "../../liveConsole/LiveConsoleControl";
import { getQueryParams, isDeveloper, usePermission } from "../../../helpers/common";
import { getSearchQueryData } from "../../../helpers/session";
import { DEFAULT_MAP_LOCATION } from "../../../constants";
import MapOverlay from "./MapOverlay";
import ReplayEGListControl from "../replay/ReplayEGListControl";
import { bindActionCreators } from "redux";
import { showOverlay, hideOverlay } from "../../../actions/toast";
import replayImage from '../../../../assets/images/replay_box.svg';
import { getSettings, updateSettings } from "../../../actions/user";
import { connectSocket } from "../../../actions/socket";
import store from "../../../store";
import { Row } from "react-bootstrap";
import { ToggleSwitch } from "../../widgets";

class MapView extends Component {
  constructor(props) {
    super(props);
    this.map = null;
    this.google = null;
    this.drawingControl = null;
    this.analyzeControl = null;
    this.workWindowControl = null;
    this.replayControl = null;
    this.mapMarker = null;
    this.mapKPIControl = null;
    this.LiveConsoleControl = null;
    this.replayEGListControl = null;
    this.state = {
      mapUpdating: false,
      searchQuery: null,
      refreshActive: false
    };

    this.loadMap = this.loadMap.bind(this);
    this.addEventListener = this.addEventListener.bind(this);
    this.getPostion = this.getPostion.bind(this);
    this.handleLoadHistory = this.handleLoadHistory.bind(this);
    this.handleSeedHistory = this.handleSeedHistory.bind(this);
    this.handleLogisticsHistory = this.handleLogisticsHistory.bind(this);
    this.handleLiveConsole = this.handleLiveConsole.bind(this);
    this.toggleAutoRefresh = this.toggleAutoRefresh.bind(this);
    this.handleAutoRefresh = this.handleAutoRefresh.bind(this);
  }

  componentDidMount() {
    this.loadMap();
    this.createControls();
    this.addEventListener();
    const queryParameters = getQueryParams();
    const searchQuery = getSearchQueryData(queryParameters['searchId']);
    if (searchQuery) {
      this.setState({ searchQuery });
    }
  }

  loadMap() {
    if (!(this.props && this.props.google)) return;
    const { google, position } = this.props;
    const maps = google.maps;
    const mapRef = this.refs.map;
    const node = ReactDOM.findDOMNode(mapRef);
    this.google = google;

    const mapConfig = {
      center: position ? position.center : DEFAULT_MAP_LOCATION,
      zoom: position ? position.zoom : 14,
      mapTypeId: "hybrid",
      panControl: false,
      streetViewControl: false,
      mapTypeControl: false,
      fullscreenControl: true,
      zoomControl: true,
      fullscreenControlOptions: {
        position: google.maps.ControlPosition.BOTTOM_RIGHT,
      },
    };

    this.map = new maps.Map(node, mapConfig);
    this.map.getControlBox = (position) =>
      getControlBox(this.map, google, position);

    // Map custom controls
    this.mapMarker = new MapMarker(this.map, google);
    this.mapKPIControl = new MapKPIControl(this.map, google);
    this.replayEGListControl = new ReplayEGListControl(this.map, google);
    this.replayControl = new ReplayControl(this.map, google);
    this.replayControl.setMapMarker(this.mapMarker);
    this.replayControl.setReplayEGListControl(this.replayEGListControl);
    this.analyzeControl = new AnalyzeControl(this.map, google);
    this.workWindowControl = new WorkWindowControl(this.map, google);
    this.drawingControl = new DrawingControl(this.map, google);
    this.drawingControl.setMapMarker(this.mapMarker);
    this.drawingControl.setReplay(this.replayControl);
    this.LiveConsoleControl = new LiveConsoleControl(this.map, google);
    setDrawingInstance(this.drawingControl);
    this.props.actions.getSettings();
    // map load completed
    if (this.props.onLoaded) this.props.onLoaded();
  }

  createControls() {
    if (!this.map) return;

    const allow = usePermission();
    const isAllowLoadHistory = allow("load-history", "view") ? true : false;
    const isAllowAnalyze = allow("analyze", "view") ? true : false;
    const isAllowWorkWindow = allow("workwindow", "view") ? true : false;
    const isAllowReplay = allow("replay", "view") ? true : false;

    // Right Top controls
    const controls = [
      isAllowAnalyze && {
        name: "ctlAnalyze",
        component: (
          <RoundControl
            className="mt-2"
            iconClassName="icon icon-analyze fa fa-flip-horizontal"
            title={"Analyze"}
            onClick={() => {
              !this.analyzeControl.isOpen()
                ? this.analyzeControl.setHistory(this.props.history).open()
                : this.analyzeControl.close();
            }}
          />
        ),
      },
      isAllowWorkWindow && {
        name: "ctlWorkWindow",
        component: (
          <RoundControl
            iconClassName="icon icon-ww-inactive"
            title={"Work Window"}
            onClick={() => {
              this.replayControl.close();
              !this.workWindowControl.isOpen()
                ? this.workWindowControl.open()
                : this.workWindowControl.close();
            }}
          />
        ),
      },
      isAllowReplay && {
        name: "ctlReplay",
        component: (
          <RoundControl
            styles={{ width: '32px', marginTop: '-9px', marginRight: '-1px' }}
            srcUrl={replayImage}
            // iconClassName="fa fa-history"
            title={"Replay"}
            onClick={() => {
              this.workWindowControl.close();
              !this.replayControl.isOpen()
                ? this.replayControl.open()
                : this.replayControl.close();
            }}
          />
        ),
      },
      isAllowLoadHistory && {
        name: "ctlLoadHistory",
        component: (
          <RoundControl
            // iconClassName="fa fa-cubes"
            title={"Load History"}
            // onClick={this.props.setLoadHistoryShowModal}
            srcUrl={require("../../../../assets/images/load_history.svg")}
            onClick={this.handleLoadHistory}
          />
        ),
      },
      isAllowLoadHistory && {
        name: "ctlSeedHistory",
        component: (
          <RoundControl
            // iconClassName="fa fa-leaf"
            title={"Seed History"}
            className={'seed_history_icon'}
            style={{ position: 'absolute', top: '-50px' }}
            // onClick={this.props.setLoadHistoryShowModal}
            srcUrl={require("../../../../assets/images/seed_history.svg")}
            onClick={this.handleSeedHistory}
          />
        ),
      },
      isAllowLoadHistory && {
        name: "ctlLogisticsHistory",
        component: (
          <RoundControl
            // iconClassName="fa fa-leaf"
            title={"Logistics History"}
            style={{ position: 'absolute', top: '-50px' }}
            // onClick={this.props.setLoadHistoryShowModal}
            srcUrl={require("../../../../assets/images/logistics.svg")}
            onClick={this.handleLogisticsHistory}
          />
        ),
      }
    ];
    const controlBox = this.map.getControlBox(
      this.google.maps.ControlPosition.RIGHT_TOP
    );

    for (const item of controls)
      controlBox.add({ ...item, className: "map-control" });

    // Right Bottom controls
    const controls1 = [
      {
        name: "ctlMapType",
        component: <Provider store={store}><MapTypeControl map={this.map} /></Provider>,
      },
      {
        name: "ctlAutoZoom",
        index: 9,
        component: <AutoZoomToggleControl />,
      },
      isDeveloper() && {
        name: "ctlLiveConsole",
        index: 10,
        component: <LiveConsoleIcon onClick={this.handleLiveConsole} />,
      },
    ];
    const controlBox1 = this.map.getControlBox(
      this.google.maps.ControlPosition.RIGHT_BOTTOM
    );
    for (const item of controls1)
      controlBox1.add({ ...item, className: "map-control" });
  }

  addEventListener() {
    const me = this;
    this.mapMarker.addListener("marker_click", null, ({ data }) => {
      const control = me.map.getControlBox(
        me.google.maps.ControlPosition.TOP_CENTER
      );
      if (control.getControl("drawingtools")) return;
      me.mapKPIControl.add({ ...data, farmId: me.props.farmId });
    });
    this.mapMarker.addListener("removed_markers", null, (removedIds) =>
      this.mapKPIControl.remove(removedIds)
    );

    this.visibilityHandler = this.handleVisibility.bind(this);
    document.addEventListener("visibilitychange", this.visibilityHandler);
  }

  componentDidUpdate(prevProps) {
    const prevMapType = prevProps && prevProps.map_type ? prevProps.map_type : "satellite";
    if (prevProps.farmId !== this.props.farmId) {
      const prevPosition = this.getPostion();
      const { position } = this.props;
      if (position) {
        this.map.setCenter(position.center);
        this.map.setZoom(position.zoom);
      }
      if (this.props.onPosition)
        this.props.onPosition(prevPosition, prevProps.farmId);
    }

    if (this.replayControl && prevProps.selectedPlanType !== this.props.selectedPlanType) {
      this.replayControl.close();
    }

    const controlBox = this.map.getControlBox(
      this.google.maps.ControlPosition.RIGHT_TOP
    );

    const ctlLoadHistory = controlBox.getControl("ctlLoadHistory");
    const ctlSeedHistory = controlBox.getControl("ctlSeedHistory");
    const ctlLogisticsHistory = controlBox.getControl("ctlLogisticsHistory");



    if (ctlLoadHistory) {
      if (this.props.selectedPlanType === 5) {
        ctlLogisticsHistory.classList.remove("hide");
        ctlLoadHistory.classList.add("hide");
        ctlSeedHistory.classList.add("hide");
        ctlLogisticsHistory.style.top = '160px';
      } else if (this.props.selectedPlanType === 2) {
        ctlSeedHistory.classList.remove("hide");
        ctlSeedHistory.style.top = '160px';
        ctlLoadHistory.classList.add("hide");
        ctlLogisticsHistory.classList.add("hide");
        // ctlLogisticsHistory.style.top = '208px';
      } else {
        ctlLoadHistory.classList.remove("hide");
        // ctlSeedHistory.style.top = '160px';
        ctlSeedHistory.classList.add("hide");
        ctlLogisticsHistory.classList.add("hide");
        // ctlLogisticsHistory.style.top = '160px';
      }

      if (this.props.isAllPlan) {
        ctlLogisticsHistory.classList.remove("hide");
        ctlSeedHistory.classList.remove("hide");
        ctlLoadHistory.classList.remove("hide");
      }
    }

    if (!this.analyzeControl) return;
    if (prevProps.analyze.disabled !== this.props.analyze.disabled) {
      if (this.props.analyze.disabled) this.analyzeControl.close();


      const ctlAnalyze = controlBox.getControl("ctlAnalyze");
      if (ctlAnalyze)
        this.props.analyze.disabled
          ? ctlAnalyze.classList.add("hidden")
          : ctlAnalyze.classList.remove("hidden");

      const ctlLoadHistory = controlBox.getControl("ctlLoadHistory");
      if (ctlLoadHistory)
        this.props.analyze.disabled
          ? ctlLoadHistory.classList.add("hidden")
          : ctlLoadHistory.classList.remove("hidden");
    }

    if (
      !this.props.analyze.disabled &&
      prevProps.analyze.show !== this.props.analyze.show
    )
      this.props.analyze.show
        ? this.analyzeControl.open()
        : this.analyzeControl.close();

    const container = document.getElementById(`$control_ctlReplay`);
    if (this.state.searchQuery && container) this.showReplayBox();

    if (prevProps.recentSummary !== this.props.recentSummary) {
      const { replayBox: { search_id }, workWindow: { show } } = this.props.recentSummary;
      if (search_id) this.showReplayBox();
      if (show) this.showWorkWindow();
    }
    const mapType = this.props.userProps && this.props.userProps.map_type ? this.props.userProps.map_type : "satellite";
    if (prevMapType !== mapType) {
      this.map.setOptions({ mapTypeId: mapType });
    }

    if (prevProps.userProps !== this.props.userProps) {
      if (this.props && this.props.userProps && this.props.userProps.hasOwnProperty("isRefresh")) {
        localStorage.setItem("mapAutoRefresh", this.props.userProps.isRefresh);
      }
    }
  }

  showReplayBox() {
    this.props.actions.showOverlay({ text: "Loading..." });
    const allow = usePermission();
    const isAllowReplay = allow("replay", "view") ? true : false;
    if (!this.replayControl.isOpen() && isAllowReplay) {
      if (this.workWindowControl) this.workWindowControl.close();
      this.replayControl.open()
      this.setState({ searchQuery: null });
    }
    this.props.actions.hideOverlay();
  }

  showWorkWindow() {
    this.props.actions.showOverlay({ text: "Loading..." });
    const allow = usePermission();
    const isAllowWorkWindow = allow("workwindow", "view") ? true : false;
    if (!this.workWindowControl.isOpen() && isAllowWorkWindow) {
      if (this.replayControl) this.replayControl.close()
      this.workWindowControl.open();
    }
    this.props.actions.hideOverlay();
  }

  handleVisibility() {
    const visible = document.visibilityState === "visible";
    const setStatus = (status) =>
      this.setState({ ...this.state, mapUpdating: status });

    setStatus(true);
    this.mapMarker.suspendAndResumeMarkers(visible);
    if (visible) setTimeout(() => setStatus(false), 1000);
    else setStatus(false);
  }

  getPostion() {
    const center = this.map.getCenter();
    if (center) {
      return {
        center: { lat: center.lat(), lng: center.lng() },
        zoom: this.map.getZoom(),
      };
    }
    else {
      return {
        center: { lat: 0, lng: 0 },
        zoom: this.map.getZoom(),
      };
    }
  }

  handleLoadHistory() {
    const {
      farmId,
      crewId,
      egId,
      analyze: { isTruck },
    } = this.props;
    let newTabUrl = null;
    if (egId)
      newTabUrl = `/home/farm/${farmId}/eg/loadhistory/${egId}/${crewId}/${isTruck}`;
    else if (crewId)
      newTabUrl = `/home/farm/${farmId}/crew/loadhistory/${crewId}`;
    else newTabUrl = `/home/farm/${farmId}/loadhistory`;
    this.handleOpenTab(newTabUrl);
  }

  handleSeedHistory() {
    let newTabUrl = null;
    const { farmId, crewId, egId, analyze: { isTruck, categeryId } } = this.props;

    if (egId && categeryId === 4) newTabUrl = `/home/farm/${farmId}/seedhistory/eg/${isTruck}/${egId}?catId=${categeryId}`;
    else if (egId && categeryId !== 4) newTabUrl = `/home/farm/${farmId}/seedhistory/eg/${isTruck}/${egId}`;
    else if (crewId) newTabUrl = `/home/farm/${farmId}/seedhistory/crew/0/0/${crewId}`;
    else newTabUrl = `/home/farm/${farmId}/seedhistory`;
    this.handleOpenTab(newTabUrl);
  }

  handleLogisticsHistory() {
    let newTabUrl = null;
    const { farmId, crewId, egId, analyze: { isTruck } } = this.props;

    if (egId) newTabUrl = `/home/farm/${farmId}/logisticshistory/eg/${isTruck}/${egId}/${crewId}`;
    else if (crewId) newTabUrl = `/home/farm/${farmId}/logisticshistory/crew/0/0/${crewId}`;
    else newTabUrl = `/home/farm/${farmId}/logisticshistory`;
    this.handleOpenTab(newTabUrl);
  }

  handleOpenTab(newTabUrl) {
    const isLoadHistoryWindow = this.props.userProps &&
      this.props.userProps.is_load_history_window ? this.props.userProps.is_load_history_window : false;
    if (isLoadHistoryWindow) {
      window.open(newTabUrl, "", "width=900,height=800,toolbar=no,menubar=no,location=no,status=no,scrollbars=yes,resizable=yes");
    } else {
      window.open(newTabUrl, "_blank");
    }
  }

  handleLiveConsole() {
    !this.LiveConsoleControl.isOpen()
      ? this.LiveConsoleControl.open()
      : this.LiveConsoleControl.close();
  }

  toggleAutoRefresh() {
    const { actions, userProps } = this.props;
    const isRefresh = userProps && userProps.hasOwnProperty("isRefresh") ? userProps && userProps.isRefresh : false;
    actions.updateSettings({ userProperties: { ...userProps, isRefresh: !isRefresh } });
  }

  handleAutoRefresh() {
    if (!this.state.refreshActive) {
      this.setState({ refreshActive: !this.state.refreshActive });
    } else {
      this.setState({ refreshActive: this.state.refreshActive });
    }
    const { userProps } = this.props;
    localStorage.setItem("mapAutoRefresh", true);
    const isRefresh = userProps && userProps.hasOwnProperty("isRefresh") ? userProps && userProps.isRefresh : false;
    setTimeout(() => {
      localStorage.setItem("mapAutoRefresh", isRefresh);
      this.setState({ refreshActive: false });
    }, 10000);
  }

  componentWillUnmount() {
    if (this.mapMarker) this.mapMarker.dispose();
    if (this.replayControl) this.replayControl.close();
    if (this.workWindowControl) this.workWindowControl.close();
    if (this.analyzeControl) this.analyzeControl.close();
    if (this.drawingControl) this.drawingControl.dispose();
    if (this.props.onUnload) this.props.onUnload();
    if (this.props.onPosition)
      this.props.onPosition(this.getPostion(), this.props.farmId);
    if (this.visibilityHandler)
      document.removeEventListener("visibilitychange", this.visibilityHandler);
  }

  render() {
    const isAutoRefresh = this.props.userProps && this.props.userProps.isRefresh ? this.props.userProps.isRefresh : false;
    if (this.props.userProps && !this.props.userProps.hasOwnProperty("isRefresh")) {
      const { actions, userProps } = this.props;
      actions.updateSettings({ userProperties: { ...userProps, isRefresh: true } });
      actions.connectSocket();
    }
    const isAnalyzeOpen = (this.analyzeControl && this.analyzeControl.isOpen());
    return (
      <>
        <div
          ref="map"
          id="map-google"
          className="mobile-h-100-scroll"
          google={this.props.google}
        >
          loading map...
        </div>
        <MapOverlay show={this.state.mapUpdating} text="Updating..." />

        <Row className={`map_refresh-tile  ${isAnalyzeOpen ?
          "isAnalyze-open animate animate__slideInRight" : ""}`}>
          <span
            className={`d-flex auto-refresh_icon ${isAutoRefresh ? "isDisabled" : ""} animate  ${this.state.refreshActive ? "animate__rotateIn" : ""}`}
          >
            <i
              title="Refresh"
              className={`fa fa-refresh btn-refresh cursor-pointer`}
              aria-hidden="true"
              onClick={this.handleAutoRefresh}
            />
          </span>

          <div className="auto_refresh-hldr">
            <span className="label">Auto refresh</span>
            <ToggleSwitch
              className="switch"
              title={
                isAutoRefresh
                  ? "Disable auto refresh"
                  : "Enable auto refresh"
              }
              onChange={this.toggleAutoRefresh}
              checked={isAutoRefresh}
            />

          </div>

        </Row>
      </>
    );
  }
}

const mapStateToProps = (state) => ({
  selectedPlanType: state.plan.selectedPlanType,
  recentSummary: state.analyze.recentSummary,
  farmSettings: state.farm.farmSettings.farm_config,
  userProps: state.user.userSettings.user_properties,
});

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators({
    showOverlay,
    hideOverlay,
    updateSettings,
    connectSocket,
    getSettings
  }, dispatch)
});

export default GoogleApiWrapper({
  apiKey: config.common.MAP_API_KEY,
  libraries: ["drawing"],
})(connect(mapStateToProps, mapDispatchToProps)(MapView));
